import { useQuery } from '@tanstack/react-query';
import type { ManualAudience } from '@evi/content';
import { fetchPreferences } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

export interface HelpAudience {
  isFleet: boolean;
  isHost: boolean;
  /** Whether a manual page of the given audience is visible to this user. */
  canSee: (audience: ManualAudience) => boolean;
}

/** Operator-tier host templates carried on Role.template. */
const HOST_TEMPLATES = new Set(['charger_owner', 'property_manager', 'building_admin']);

/**
 * Role-aware gate for the Help manual, split by audience:
 *
 *   - 'driver' pages show to everyone.
 *   - 'fleet' pages show to fleet members (`user.fleet_id` set, the same
 *     signal useCurrentMode gates the FLEET / PUBLIC pill on).
 *   - 'host' pages show to charger hosts: either an operator-tier
 *     `Role.template` (charger_owner / property_manager / building_admin) OR a
 *     self-service My Chargers owner (`has_resident_chargers` from
 *     /me/preferences, the resident who shares a charger and earns as a host).
 *
 * A user who is both a fleet member and a host sees both groups.
 */
export function useHelpAudience(): HelpAudience {
  const user = useAuthStore((s) => s.user);
  // Self-service host signal. Same cached query SettingsPage reads, so this
  // rarely costs a fetch; host gating falls back to the role template until
  // the preference lands, then widens when it does.
  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: fetchPreferences,
    staleTime: 60_000,
  });

  const isFleet = Boolean(user?.fleet_id);
  const isHost = HOST_TEMPLATES.has(user?.template ?? '')
    || prefsQ.data?.has_resident_chargers === true;

  const canSee = (audience: ManualAudience): boolean => {
    switch (audience) {
      case 'fleet': return isFleet;
      case 'host': return isHost;
      case 'driver':
      default: return true;
    }
  };

  return { isFleet, isHost, canSee };
}
